<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateProperty',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const resource = ref(null);
  const editor = ref(null);
  const container = ref(null);
  const formName = ref('meter');
  const formId = ref(null);
  const propertyUnitTypes = ref(null);
  const units = ref(['ha', 'm2']);
  const resourceTitle = ref($fn.value.pgettext('Property', 'Property'));
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    container: container.value,
    formName: formName.value,
    formId: formId.value,
    propertyUnitTypes: propertyUnitTypes.value,
    units: units.value,
    resourceTitle: resourceTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_resource = async () => {
    return $fn.value.schemaDefaults('CreateProperty', { owner: null });
  };
  const $$load_editor = async () => {
    return resource.value.editor();
  };
  const $$load_container = async () => {
    return {
      self: $fn.value.urlFor('create_property'),
      object: 'list',
      data: [],
    };
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_propertyUnitTypes = async () => {
    return $fn.value
      .fetch('list_property_types')
      .then((p) => _.orderBy(p, ['name']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'container' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'propertyUnitTypes' },
      { name: 'units' },
      { name: 'resourceTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      resource: $$load_resource,
      editor: $$load_editor,
      container: $$load_container,
      formId: $$load_formId,
      propertyUnitTypes: $$load_propertyUnitTypes,
    },
    ux: {},
    dataRefs: {
      resource,
      editor,
      container,
      formName,
      formId,
      propertyUnitTypes,
      units,
      resourceTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Property', 'New property') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_kd8gsilen_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_ojpl3fqbv_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_navigate_x3gcthgq0_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.property',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const act_1040167445267876_reloadSlickgrid_lh2agq698_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'properties',
    };
  };
  const act_1040167445267876_rest_3dbi1ll1w_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_kd8gsilen =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_kd8gsilen_getActionArgs,
    });
  const inst_evh_7315092382398562_close_dux27v9io =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_kd8gsilen,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_dux27v9io],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_d5s0ep35v =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_kcaui3u6k =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_d5s0ep35v,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_kcaui3u6k],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_qqxuff9zz =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_pw8zzk4td =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_qqxuff9zz,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_pw8zzk4td],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_ojpl3fqbv =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_ojpl3fqbv_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_h5bumlz5z =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_ojpl3fqbv,
      event: 'saved',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_navigate_x3gcthgq0 =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_x3gcthgq0_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_f2yz0uy6x =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_x3gcthgq0,
      event: 'saved',
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadSlickgrid_lh2agq698 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_lh2agq698_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_7f8mru50v =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_lh2agq698,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_rest_3dbi1ll1w = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_3dbi1ll1w_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_saved_h5bumlz5z,
      inst_evh_1040167445267876_saved_f2yz0uy6x,
      inst_evh_1040167445267876_saved_7f8mru50v,
    ],
  });
  const inst_evh_1040167445267876_submit_4z56hlpo3 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_3dbi1ll1w,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_4z56hlpo3],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_uezkx7eyb =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_zb6bdqggk =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_uezkx7eyb,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_zb6bdqggk],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_dux27v9io,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_kcaui3u6k,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_pw8zzk4td,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_h5bumlz5z,
    inst_evh_1040167445267876_saved_f2yz0uy6x,
    inst_evh_1040167445267876_saved_7f8mru50v,
    inst_evh_1040167445267876_submit_4z56hlpo3,
    create: inst_evh_1040167445267876_submit_4z56hlpo3,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_zb6bdqggk,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_dux27v9io = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_dux27v9io.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_kcaui3u6k = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_kcaui3u6k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_pw8zzk4td = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_pw8zzk4td.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_h5bumlz5z = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_h5bumlz5z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_f2yz0uy6x = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_f2yz0uy6x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_7f8mru50v = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_7f8mru50v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_4z56hlpo3 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_4z56hlpo3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_zb6bdqggk = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_zb6bdqggk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateProperty"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :dialogName="resourceTitle"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateProperty"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetPropertyGeneralData :property="editor">
        </FieldsetPropertyGeneralData>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          type="submit"
          :form="forms[formName]"
          :spinning="actions.create.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
